import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject, throwError, timer } from 'rxjs';
import { takeUntil, catchError, filter } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { v4 as uuidv4 } from 'uuid';
import {
  StreamingSpeechMessage,
  StreamingSpeechConfig,
  ConnectionStatus,
  StreamingSpeechError,
  StreamingSpeechErrorCode,
  TranscriptMessage,
  FinalTranscriptMessage,
  ProcessedTranscriptMessage,
  WarningMessage,
  ErrorMessage,
  LLMErrorMessage,
  MaxTimeReachedMessage,
  TimeoutMessage,
  CancelledMessage,
  StreamEndedMessage,
  isConnectionMessage,
  isStreamStartedMessage,
  isTranscriptMessage,
  isFinalTranscriptMessage,
  isProcessedTranscriptMessage,
  isStreamEndedMessage,
  isErrorMessage,
  isWarningMessage,
  isLLMErrorMessage,
  isMaxTimeReachedMessage,
  isTimeoutMessage,
  isCancelledMessage,
} from '../interfaces/streaming-speech-types';

type Float32ArrayWithArrayBuffer = Float32Array & { buffer: ArrayBuffer };

/**
 * Streaming Speech Service
 *
 * Unified service for the new TypeScript streaming speech server.
 * Provides WebSocket-based real-time speech-to-text with LLM processing support.
 */
@Injectable({
  providedIn: 'root',
})
export class StreamingSpeechService {
  private readonly serverUrl: string;
  private readonly healthUrl: string;

  private websocket: WebSocket | null = null;
  private currentSessionId: string | null = null;
  private audioContext: AudioContext | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioStream: MediaStream | null = null;
  private currentEncoding: 'LINEAR16' | 'WEBM_OPUS' = 'LINEAR16';
  private isRecording = false;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private reconnectTimer: any = null;
  private pingInterval: any = null;
  private readonly pingIntervalMs = 30000; // 30 seconds
  private llmEnabled = false; // Track if LLM is enabled for current session
  private cleanupTimeout: any = null; // Timeout for delayed cleanup after LLM processing

  // Connection state management
  private connectionReady: Promise<void> | null = null;
  private connectionResolve: (() => void) | null = null;
  private connectionReject: ((error: Error) => void) | null = null;
  private connectionTimeout: any = null;

  // Observables
  private transcriptSubject = new Subject<TranscriptMessage>();
  private finalTranscriptSubject = new Subject<FinalTranscriptMessage>();
  private processedTranscriptSubject = new Subject<ProcessedTranscriptMessage>();
  private errorSubject = new Subject<StreamingSpeechError>();
  private warningSubject = new Subject<WarningMessage>();
  private streamEndedSubject = new Subject<StreamEndedMessage>();
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>('disconnected');
  private destroy$ = new Subject<void>();

  private voiceEnergySubject = new BehaviorSubject<number>(0);
  private voiceEnergyAnalyser: AnalyserNode | null = null;
  private voiceEnergyData?: Float32ArrayWithArrayBuffer;
  private voiceEnergyAnimationFrame: number | null = null;
  private voiceEnergySourceNode: MediaStreamAudioSourceNode | null = null;

  // Public observables
  transcript$ = this.transcriptSubject.asObservable();
  finalTranscript$ = this.finalTranscriptSubject.asObservable();
  processedTranscript$ = this.processedTranscriptSubject.asObservable();
  error$ = this.errorSubject.asObservable();
  warning$ = this.warningSubject.asObservable();
  streamEnded$ = this.streamEndedSubject.asObservable();
  connectionStatus$ = this.connectionStatusSubject.asObservable();
  voiceEnergy$ = this.voiceEnergySubject.asObservable();

  constructor() {
    this.serverUrl = environment.streamingSpeechServer?.url || '';
    this.healthUrl = environment.streamingSpeechServer?.healthUrl || '';

    if (!this.serverUrl) {
      console.warn('⚠️ Streaming speech server URL not configured in environment');
    }
  }

  /**
   * Check if browser supports required features
   */
  static isBrowserSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      (window.AudioContext || (window as any).webkitAudioContext) &&
      window.WebSocket
    );
  }

  /**
   * Test connection to streaming speech server
   */
  async testConnection(): Promise<boolean> {
    if (!this.healthUrl) {
      return false;
    }

    try {
      const response = await fetch(this.healthUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.status === 'healthy' || data.status === 'ok';
      }

      return false;
    } catch (error) {
      console.error('❌ Health check failed:', error);
      return false;
    }
  }

  /**
   * Start a streaming speech session
   */
  async startSession(config: StreamingSpeechConfig): Promise<void> {
    if (!StreamingSpeechService.isBrowserSupported()) {
      throw new Error('Browser does not support required features for speech recognition');
    }

    if (!this.serverUrl) {
      throw new Error('Streaming speech server URL not configured');
    }

    try {
      // Connect WebSocket and wait for connection message
      await this.connectWebSocket();

      // Wait for connection message to be received (with server-provided sessionId)
      await this.connectionReady;

      // Determine encoding and sample rate
      this.currentEncoding = config.encoding || 'LINEAR16';
      const sampleRate = config.sampleRate || (this.currentEncoding === 'WEBM_OPUS' ? 48000 : 16000);

      // Track if LLM is enabled for this session
      this.llmEnabled = config.llmConfig?.enabled === true;
      // console.log(`🎤 LLM enabled for this session: ${this.llmEnabled}`);

      // Start audio capture
      await this.startAudioCapture(sampleRate);

      // Send start message
      await this.sendStartMessage(config);

      this.isRecording = true;

      // Start ping interval
      this.startPingInterval();
    } catch (error) {
      console.error('❌ Failed to start session:', error);
      this.cleanup();
      throw error;
    }
  }

  /**
   * Stop the current session
   *
   * Note: Keeps recording for 0.3 seconds after user stops to capture the last phrase.
   * Users often press stop before completing their last word, so this delay ensures
   * the final audio is captured and processed.
   */
  async stopSession(): Promise<void> {
    if (!this.isRecording || !this.currentSessionId) {
      return;
    }

    // Mark as stopping but keep recording for 0.3 seconds to capture last phrase
    // Users often press stop before completing their last word
    const STOP_DELAY_MS = 300; // 0.3 seconds

    // console.log(`🛑 Stop requested - continuing recording for ${STOP_DELAY_MS}ms to capture last phrase`);

    // Wait before actually stopping
    await new Promise(resolve => setTimeout(resolve, STOP_DELAY_MS));

    // Now actually stop
    this.isRecording = false;

    // Stop audio capture
    this.stopAudioCapture();

    // Stop ping interval
    this.stopPingInterval();

    // Send stop message
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.sendMessage({
        type: 'stop',
        timestamp: new Date().toISOString(),
      });
    }

    // If LLM is enabled, wait longer for processed transcript to arrive
    // LLM processing typically takes 4-5 seconds, so wait up to 15 seconds
    // If LLM is not enabled, cleanup after 1 second
    const cleanupDelay = this.llmEnabled ? 15000 : 1000;
    // console.log(`🛑 Scheduling cleanup in ${cleanupDelay}ms (LLM enabled: ${this.llmEnabled})`);

    // Clear any existing cleanup timeout
    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
    }

    this.cleanupTimeout = setTimeout(() => {
      // console.log('🛑 Cleanup timeout reached, closing WebSocket');
      this.cleanup();
    }, cleanupDelay);
  }

  /**
   * Cancel the current session
   */
  async cancelSession(reason?: string): Promise<void> {
    if (!this.currentSessionId) {
      return;
    }

    this.isRecording = false;

    // Stop audio capture
    this.stopAudioCapture();

    // Stop ping interval
    this.stopPingInterval();

    // Send cancel message
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.sendMessage({
        type: 'cancel',
        timestamp: new Date().toISOString(),
        reason: reason || 'User cancelled',
      });
    }

    // Cleanup immediately
    this.cleanup();
  }

  /**
   * Connect to WebSocket server
   */
  private async connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN && this.connectionReady) {
        resolve();
        return;
      }

      // Reset connection promise
      this.connectionReady = new Promise<void>((connectionResolve, connectionReject) => {
        this.connectionResolve = connectionResolve;
        this.connectionReject = connectionReject;
      });

      // console.log('🔗 Connecting to streaming speech server:', this.serverUrl);
      this.connectionStatusSubject.next('connecting');

      this.websocket = new WebSocket(this.serverUrl);

      this.websocket.onopen = () => {
        // console.log('✅ WebSocket opened, waiting for connection message...');
        this.reconnectAttempts = 0;
        // Do NOT resolve here - wait for connection message
        resolve(); // Resolve the connectWebSocket promise, but connectionReady waits for message
      };

      this.websocket.onmessage = event => {
        this.handleMessage(event.data);
      };

      this.websocket.onerror = error => {
        console.error('❌ WebSocket error:', error);
        this.connectionStatusSubject.next('error');
        if (this.connectionReject) {
          this.connectionReject(new Error('WebSocket connection error'));
        }
        reject(error);
      };

      this.websocket.onclose = event => {
        // console.log(`🔌 WebSocket closed (Code: ${event.code}, Reason: ${event.reason})`);
        this.connectionStatusSubject.next('disconnected');

        // Reject connection promise if still pending
        if (this.connectionReject) {
          this.connectionReject(new Error(`WebSocket closed: ${event.reason || 'Unknown reason'}`));
        }

        // Attempt reconnection if recording and not a normal closure
        if (this.isRecording && event.code !== 1000) {
          this.attemptReconnect();
        }
      };

      // Connection timeout - reject if connection message not received within 10 seconds
      this.connectionTimeout = setTimeout(() => {
        if (this.connectionReject) {
          const error = new Error('Connection message timeout - server did not respond');
          this.connectionReject(error);
          this.errorSubject.next({
            code: StreamingSpeechErrorCode.SERVICE_UNAVAILABLE,
            error: 'Connection timeout - server did not send connection message',
            severity: 'error',
            recoverable: true,
            suggestedAction: 'retry',
          });
          reject(error);
        }
      }, 10000);
    });
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');
      this.errorSubject.next({
        code: StreamingSpeechErrorCode.CONNECTION_LOST,
        error: 'Connection lost and reconnection failed',
        severity: 'error',
        recoverable: false,
        suggestedAction: 'reconnect',
      });
      this.cleanup();
      return;
    }

    this.reconnectAttempts++;
    const delayMs = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);

    // console.log(`🔄 Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delayMs}ms`);

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.connectWebSocket();
        // Re-send start message if we have a session
        if (this.currentSessionId) {
          // Note: In a real scenario, we'd need to store the config to re-send
          // For now, we'll just reconnect and let the user restart
        }
      } catch (error) {
        console.error('❌ Reconnection failed:', error);
        this.attemptReconnect();
      }
    }, delayMs);
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: string | ArrayBuffer): void {
    // Handle binary data (shouldn't be received from server)
    if (data instanceof ArrayBuffer) {
      console.warn('⚠️ Received unexpected binary data from server');
      return;
    }

    try {
      const message: StreamingSpeechMessage = JSON.parse(data as string);

      // Route message to appropriate handler
      if (isConnectionMessage(message)) {
        this.handleConnectionMessage(message);
      } else if (isStreamStartedMessage(message)) {
        this.handleStreamStartedMessage(message);
      } else if (isTranscriptMessage(message)) {
        this.transcriptSubject.next(message);
      } else if (isFinalTranscriptMessage(message)) {
        this.finalTranscriptSubject.next(message);
      } else if (isProcessedTranscriptMessage(message)) {
        // console.log('📝 Service received processed transcript message:', {
        //     processedTranscript: message.processedTranscript?.substring(0, 100),
        //     rawTranscript: message.rawTranscript?.substring(0, 100),
        //     llmModel: message.llmModel
        // });
        this.processedTranscriptSubject.next(message);

        // Processed transcript received - can cleanup now
        // console.log('📝 Processed transcript received, cleaning up WebSocket');
        if (this.cleanupTimeout) {
          clearTimeout(this.cleanupTimeout);
          this.cleanupTimeout = null;
        }
        // Delay cleanup slightly to ensure message is processed by subscribers
        setTimeout(() => {
          this.cleanup();
        }, 500);
      } else if (isStreamEndedMessage(message)) {
        this.handleStreamEndedMessage(message);
      } else if (isErrorMessage(message)) {
        this.handleErrorMessage(message);
      } else if (isWarningMessage(message)) {
        this.warningSubject.next(message);
      } else if (isLLMErrorMessage(message)) {
        this.handleLLMErrorMessage(message);
      } else if (isMaxTimeReachedMessage(message)) {
        this.handleMaxTimeReachedMessage(message);
      } else if (isTimeoutMessage(message)) {
        this.handleTimeoutMessage(message);
      } else if (isCancelledMessage(message)) {
        this.handleCancelledMessage(message);
      } else {
        console.warn('⚠️ Unknown message type:', (message as any).type);
      }
    } catch (error) {
      console.error('❌ Failed to parse message:', error);
    }
  }

  /**
   * Handle connection message
   */
  private handleConnectionMessage(message: any): void {
    // console.log('🔗 Connection message:', message.message);

    // Store server-provided sessionId
    if (message.sessionId) {
      this.currentSessionId = message.sessionId;
      // console.log('🔗 Server sessionId:', this.currentSessionId);
    }

    if (message.status === 'connected') {
      this.connectionStatusSubject.next('connected');

      // Clear connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      // Resolve connection promise
      if (this.connectionResolve) {
        this.connectionResolve();
        this.connectionResolve = null;
        this.connectionReject = null;
      }
    }
  }

  /**
   * Handle stream started message
   */
  private handleStreamStartedMessage(message: any): void {
    // console.log('🎤 Stream started:', message.message || `Model: ${message.model}, Mode: ${message.processingMode}`);
  }

  /**
   * Handle stream ended message
   */
  private handleStreamEndedMessage(message: StreamEndedMessage): void {
    // console.log('🎤 Stream ended:', message.reason || 'completed');
    // Note: isRecording is already false at this point (set in stopSession after delay)
    // But don't cleanup here - wait for processed transcript if LLM is enabled
    this.stopAudioCapture();
    this.stopPingInterval();

    // Emit stream ended event for components to handle cleanup
    this.streamEndedSubject.next(message);
  }

  /**
   * Handle error message
   */
  private handleErrorMessage(message: ErrorMessage): void {
    console.error('❌ Server error:', message.error, 'Code:', message.code);

    const error: StreamingSpeechError = {
      code: message.code,
      error: message.error,
      severity: message.severity || 'error',
      recoverable: message.recoverable ?? true,
      suggestedAction: message.suggestedAction || 'retry',
      retryAfterMs: message.retryAfterMs,
      details: message.details,
      rawTranscript: message.rawTranscript,
    };

    this.errorSubject.next(error);

    // If critical error, stop recording
    if (message.severity === 'critical') {
      this.isRecording = false;
      this.stopAudioCapture();
    }
  }

  /**
   * Handle LLM error message
   */
  private handleLLMErrorMessage(message: LLMErrorMessage): void {
    console.warn('⚠️ LLM error:', message.error);

    // LLM errors are non-fatal - we still have the raw transcript
    this.errorSubject.next({
      code: message.code,
      error: message.error,
      severity: 'warning',
      recoverable: true,
      suggestedAction: 'use_raw_transcript',
      rawTranscript: message.rawTranscript,
    });
  }

  /**
   * Handle max time reached message
   */
  private handleMaxTimeReachedMessage(message: MaxTimeReachedMessage): void {
    console.warn('⚠️ Max recording time reached:', message.maxRecordingTime, 'seconds');

    this.warningSubject.next({
      type: 'warning',
      code: StreamingSpeechErrorCode.MAX_DURATION_EXCEEDED,
      message: message.message,
      severity: 'warning',
      recoverable: true,
      suggestedAction: 'continue',
      sessionId: message.sessionId,
      timestamp: message.timestamp,
    });

    // Automatically stop after grace period
    setTimeout(() => {
      if (this.isRecording) {
        this.stopSession();
      }
    }, message.gracePeriodSeconds * 1000);
  }

  /**
   * Handle timeout message
   */
  private handleTimeoutMessage(message: TimeoutMessage): void {
    console.error('❌ Timeout:', message.message);

    this.errorSubject.next({
      code: message.code,
      error: message.message,
      severity: 'error',
      recoverable: true,
      suggestedAction: 'retry',
    });
  }

  /**
   * Handle cancelled message
   */
  private handleCancelledMessage(message: CancelledMessage): void {
    // console.log('🚫 Session cancelled:', message.reason);
    this.isRecording = false;
    this.cleanup();
  }

  /**
   * Start audio capture using Web Audio API for LINEAR16 or MediaRecorder for WEBM_OPUS
   */
  private async startAudioCapture(sampleRate: number): Promise<void> {
    try {
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;

      if (this.currentEncoding === 'WEBM_OPUS') {
        // Use MediaRecorder for WEBM_OPUS
        const options: MediaRecorderOptions = {
          mimeType: 'audio/webm;codecs=opus',
        };

        if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
          options.mimeType = 'audio/webm';
        }

        this.mediaRecorder = new MediaRecorder(this.audioStream, options);

        this.mediaRecorder.ondataavailable = async event => {
          if (event.data.size > 0 && this.isRecording && this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            try {
              // Check for backpressure before sending
              if (this.websocket.bufferedAmount > 65536) {
                console.warn('⚠️ WebSocket buffer backing up:', this.websocket.bufferedAmount, 'bytes - skipping chunk');
                return;
              }

              const arrayBuffer = await event.data.arrayBuffer();
              this.websocket.send(arrayBuffer);
            } catch (error) {
              console.error('❌ Failed to send audio chunk:', error);
              this.errorSubject.next({
                code: StreamingSpeechErrorCode.NETWORK_ERROR,
                error: 'Failed to send audio data',
                severity: 'error',
                recoverable: true,
                suggestedAction: 'reconnect',
              });
            }
          }
        };

        this.mediaRecorder.onerror = event => {
          console.error('MediaRecorder error:', event);
          this.errorSubject.next({
            code: StreamingSpeechErrorCode.AUDIO_PROCESSING_ERROR,
            error: 'Audio recording error',
            severity: 'error',
            recoverable: true,
            suggestedAction: 'retry',
          });
        };

        this.mediaRecorder.start(100); // Send chunks every 100ms
        // console.log('🎤 Audio capture started with WEBM_OPUS encoding');

        if (AudioContextClass) {
          try {
            this.audioContext = new AudioContextClass();
            const energySource = this.audioContext.createMediaStreamSource(this.audioStream);
            this.startVoiceEnergyTracking(this.audioContext, energySource);
          } catch (error) {
            console.warn('⚠️ Unable to initialize voice energy monitoring for WEBM_OPUS:', error);
          }
        }
      } else {
        // Use Web Audio API for LINEAR16
        if (!AudioContextClass) {
          throw new Error('AudioContext not supported');
        }

        this.audioContext = new AudioContextClass({ sampleRate: sampleRate });

        // Create MediaStreamSource from audio stream
        const source = this.audioContext.createMediaStreamSource(this.audioStream);
        this.startVoiceEnergyTracking(this.audioContext, source);

        // Create ScriptProcessorNode for PCM conversion
        // Buffer size: 4096 samples = ~256ms at 16kHz (recommended chunk size)
        // Note: ScriptProcessorNode is deprecated but still widely supported
        this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);

        this.scriptProcessor.onaudioprocess = event => {
          // Only send if recording and WebSocket is open
          if (!this.isRecording || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
            return;
          }

          // Get audio data from input buffer (mono channel)
          const inputBuffer = event.inputBuffer.getChannelData(0);
          const pcm16 = new Int16Array(inputBuffer.length);

          // Convert Float32 (-1.0 to 1.0) to Int16 (-32768 to 32767)
          for (let i = 0; i < inputBuffer.length; i++) {
            // Clamp value to [-1, 1] range
            const s = Math.max(-1, Math.min(1, inputBuffer[i]));
            // Convert to 16-bit integer (little-endian)
            pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }

          // Send PCM16 buffer as binary data
          // Chunk size: 4096 samples * 2 bytes = 8192 bytes (~256ms at 16kHz)
          // This is within the recommended 3200-6400 bytes range per 100-200ms
          // We send larger chunks less frequently for efficiency
          try {
            // Check for backpressure before sending
            if (this.websocket.bufferedAmount > 65536) {
              console.warn('⚠️ WebSocket buffer backing up:', this.websocket.bufferedAmount, 'bytes - skipping chunk');
              return;
            }

            this.websocket.send(pcm16.buffer);
          } catch (error) {
            console.error('❌ Failed to send audio chunk:', error);
            // Emit error if WebSocket send fails
            this.errorSubject.next({
              code: StreamingSpeechErrorCode.NETWORK_ERROR,
              error: 'Failed to send audio data',
              severity: 'error',
              recoverable: true,
              suggestedAction: 'reconnect',
            });
          }
        };

        // Connect source to processor, processor to destination (required for ScriptProcessorNode)
        source.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.audioContext.destination);

        // console.log('🎤 Audio capture started with LINEAR16 PCM conversion');
      }
    } catch (error) {
      console.error('❌ Failed to start audio capture:', error);

      this.stopVoiceEnergyTracking();

      // Clean up on error
      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }
      if (this.audioContext) {
        this.audioContext.close().catch(() => {});
        this.audioContext = null;
      }
      if (this.mediaRecorder) {
        this.mediaRecorder.stop();
        this.mediaRecorder = null;
      }
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
        this.audioStream = null;
      }

      this.errorSubject.next({
        code: StreamingSpeechErrorCode.AUDIO_PROCESSING_ERROR,
        error: error instanceof Error ? error.message : 'Failed to access microphone',
        severity: 'error',
        recoverable: true,
        suggestedAction: 'retry',
      });

      throw new Error('Failed to access microphone');
    }
  }

  /**
   * Stop audio capture (both MediaRecorder and Web Audio API)
   */
  private stopAudioCapture(): void {
    this.stopVoiceEnergyTracking();

    // Stop MediaRecorder if used (WEBM_OPUS)
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    }

    // Stop Web Audio API if used (LINEAR16)
    // Disconnect ScriptProcessorNode
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    // Close AudioContext
    if (this.audioContext) {
      this.audioContext.close().catch(error => {
        console.warn('⚠️ Error closing AudioContext:', error);
      });
      this.audioContext = null;
    }

    // Stop media stream tracks
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
  }

  /**
   * Send start message to server
   */
  private async sendStartMessage(config: StreamingSpeechConfig): Promise<void> {
    // Validate WebSocket state
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    // Verify connection promise resolved (connection message received)
    if (!this.currentSessionId) {
      throw new Error('Connection not established - sessionId not received from server');
    }

    // Determine encoding with default to LINEAR16 for backward compatibility
    const encoding = config.encoding || 'LINEAR16';

    // Auto-set sample rate based on encoding if not provided
    let sampleRate = config.sampleRate;
    if (!sampleRate) {
      sampleRate = encoding === 'WEBM_OPUS' ? 48000 : 16000;
    }

    // Build start message according to API spec (no sessionId)
    const startMessage = {
      type: 'start',
      timestamp: new Date().toISOString(),
      processingMode: config.processingMode,
      model: config.model,
      encoding: encoding,
      sampleRate: sampleRate,
      patientId: config.patientId,
      patientUuid: config.patientUuid,
      practiceId: config.practiceId,
      practiceTenantId: config.practiceTenantId,
      fieldContext: config.fieldContext,
      componentName: config.componentName,
      maxRecordingTime: config.maxRecordingTime,
      llmConfig: config.llmConfig, // Include LLM config if provided
      parentEventId: config.parentEventId, // Include workflow correlation ID when provided
    };

    try {
      this.sendMessage(startMessage);
    } catch (error) {
      console.error('❌ Failed to send start message:', error);
      throw new Error('Failed to send start message to server');
    }
  }

  /**
   * Send message to server
   */
  private sendMessage(message: any): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ Cannot send message - WebSocket not connected');
    }
  }

  /**
   * Initialize audio analyser for voice energy monitoring
   */
  private startVoiceEnergyTracking(context: AudioContext, source: MediaStreamAudioSourceNode): void {
    if (!context || !source) {
      return;
    }

    this.stopVoiceEnergyTracking();

    try {
      this.voiceEnergySourceNode = source;
      this.voiceEnergyAnalyser = context.createAnalyser();
      this.voiceEnergyAnalyser.fftSize = 256;
      source.connect(this.voiceEnergyAnalyser);
      const buffer = new ArrayBuffer(this.voiceEnergyAnalyser.fftSize * Float32Array.BYTES_PER_ELEMENT);
      this.voiceEnergyData = new Float32Array(buffer) as Float32ArrayWithArrayBuffer;

      const updateEnergy = () => {
        if (!this.voiceEnergyAnalyser || !this.voiceEnergyData) {
          return;
        }

        const energyView = this.voiceEnergyData as any;
        this.voiceEnergyAnalyser.getFloatTimeDomainData(energyView);
        const level = this.calculateVoiceEnergyLevel(energyView as Float32Array);
        this.voiceEnergySubject.next(level);

        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          this.voiceEnergyAnimationFrame = window.requestAnimationFrame(updateEnergy);
        }
      };

      updateEnergy();
    } catch (error) {
      console.warn('⚠️ Unable to start voice energy tracking:', error);
      this.voiceEnergySubject.next(0);
    }
  }

  /**
   * Stop analyser and reset voice energy readings
   */
  private stopVoiceEnergyTracking(): void {
    if (this.voiceEnergyAnimationFrame && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(this.voiceEnergyAnimationFrame);
    }

    this.voiceEnergyAnimationFrame = null;

    if (this.voiceEnergyAnalyser) {
      this.voiceEnergyAnalyser.disconnect();
      this.voiceEnergyAnalyser = null;
    }

    if (this.voiceEnergySourceNode) {
      this.voiceEnergySourceNode.disconnect();
      this.voiceEnergySourceNode = null;
    }

    this.voiceEnergyData = undefined;
    this.voiceEnergySubject.next(0);
  }

  /**
   * Convert float buffer to normalized RMS level (0-1)
   */
  private calculateVoiceEnergyLevel(buffer: Float32Array): number {
    if (!buffer.length) {
      return 0;
    }

    let sumSquares = 0;
    for (let i = 0; i < buffer.length; i++) {
      const sample = buffer[i];
      sumSquares += sample * sample;
    }

    const rms = Math.sqrt(sumSquares / buffer.length);
    const threshold = 0.003;
    const normalized = Math.min(1, Math.max(0, (rms - threshold) * 12));
    return Number(normalized.toFixed(4));
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPingInterval(): void {
    this.stopPingInterval(); // Clear any existing interval

    let sequence = 0;
    this.pingInterval = setInterval(() => {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.sendMessage({
          type: 'ping',
          timestamp: new Date().toISOString(),
          sequence: sequence++,
        });
      }
    }, this.pingIntervalMs);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.isRecording = false;
    this.stopAudioCapture();
    this.stopPingInterval();

    // Clear cleanup timeout if it exists
    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = null;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    // Reject connection promise if still pending
    if (this.connectionReject) {
      this.connectionReject(new Error('Connection cleanup'));
      this.connectionReject = null;
      this.connectionResolve = null;
    }

    this.connectionReady = null;

    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }

    this.currentSessionId = null;
    this.reconnectAttempts = 0;
    this.llmEnabled = false; // Reset LLM enabled flag
    this.connectionStatusSubject.next('disconnected');
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.cleanup();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
